'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Image as ImageIcon } from 'lucide-react';
import { ThumbStrip } from './ReferenceSummary';
import { ProjectReferenceSheet } from './ProjectReferenceSheet';
import { cn } from '@/shared/lib/cn';
import { useClassifiedProjectReferences } from '@/features/project-reference/hooks/use-project-references';
import type { ProjectReferenceCategory } from '@/types/project-reference';

interface ProjectReferenceSelectorProps {
  projectId: string;
  category: ProjectReferenceCategory;
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  maxSelection?: number;
  disabled?: boolean;
  /** Whether this option is the currently selected mode (shows highlighted border) */
  isSelected?: boolean;
  /** Callback to navigate to the project references management page */
  onNavigateToReferences?: () => void;
}

/**
 * Compact "From reference" trigger row + selection sheet. Used by the output
 * style sub-panel. Style/materials/colors use `ReferenceSourceGroup`, which
 * embeds the same `ProjectReferenceSheet` behind a radio row.
 */
export function ProjectReferenceSelector({
  projectId,
  category,
  selectedIds,
  onSelectionChange,
  maxSelection = 3,
  disabled = false,
  isSelected = false,
  onNavigateToReferences,
}: ProjectReferenceSelectorProps) {
  const tRender = useTranslations('render.settings.projectReference');
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const { data: matchingReferences = [], isLoading } =
    useClassifiedProjectReferences(projectId, category, {
      enabled: !!projectId,
    });

  const selectedCount = selectedIds.length;

  // Image URLs of the selected references (selection order) so the trigger
  // shows the same overlapping circles as the main settings row instead of a
  // bare count.
  const selectedRefImages = selectedIds
    .map((id) => matchingReferences.find((r) => r.id === id)?.attributes.file_url)
    .filter((url): url is string => !!url);

  return (
    <>
      {/* Compact trigger inside section */}
      <button
        type="button"
        onClick={() => setIsSheetOpen(true)}
        disabled={disabled || isLoading}
        className={cn(
          'w-full flex items-center justify-between py-3 px-4 rounded-lg border-2 transition-all',
          isSelected
            ? 'border-primary bg-primary/10'
            : 'border-border hover:border-muted-foreground',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        <div className="flex items-center gap-2">
          <ImageIcon className="w-4 h-4 text-muted-foreground" />
          <span className="text-list-item-subtitle text-foreground">
            {tRender('fromReference')}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {selectedRefImages.length > 0 ? (
            <ThumbStrip images={selectedRefImages} shape="circle" />
          ) : (
            selectedCount > 0 && (
              <span className="text-chip bg-primary text-primary-foreground px-2 py-0.5 rounded-full">
                {selectedCount}
              </span>
            )
          )}
        </div>
      </button>

      <ProjectReferenceSheet
        projectId={projectId}
        category={category}
        selectedIds={selectedIds}
        onSelectionChange={onSelectionChange}
        maxSelection={maxSelection}
        open={isSheetOpen}
        onOpenChange={setIsSheetOpen}
        onNavigateToReferences={onNavigateToReferences}
      />
    </>
  );
}
